import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { getDirectoryPlumbersForNeighbourhood, type DirectoryExtraFilters } from '../../lib/api'
import { DIRECTORY_PLUMBERS_PAGE_SIZE, DIRECTORY_MAP_PLUMBER_LIMIT } from '../../lib/directoryConstants'
import { useDirectoryPlumbersInfinite } from '../../hooks/useDirectoryPlumbersInfinite'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Seo } from '../../components/Seo'
import { DirectorySidebar } from '../../components/directory/DirectorySidebar'
import { DirectoryPlumberList } from '../../components/directory/DirectoryPlumberList'
import { DIRECTORY_MAIN_GRID, DIRECTORY_PAGE_MAX, DIRECTORY_SIDEBAR_COL, slugToLabel } from './directoryUtils'
import { DirectorySearchBar } from '../../components/directory/DirectorySearchBar'
import { buildPlumberHref, toPlumberPins } from '../../lib/plumberPins'

export function DirectoryNeighbourhood() {
  const { countrySlug, countySlug, neighbourhoodSlug } = useParams<{
    countrySlug: string
    countySlug: string
    neighbourhoodSlug: string
  }>()

  const { isEmergency, isGasSafe, isWaterSafe, basePath, label } = useDirectoryMode()
  const [extraFilters, setExtraFilters] = useState<DirectoryExtraFilters>({})

  const fetchPlumbers = useCallback(
    (skip: number) =>
      getDirectoryPlumbersForNeighbourhood(
        countrySlug!, countySlug!, neighbourhoodSlug!,
        isEmergency, isGasSafe, isWaterSafe,
        { skip, limit: DIRECTORY_PLUMBERS_PAGE_SIZE },
        extraFilters,
      ),
    [countrySlug, countySlug, neighbourhoodSlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
  )

  const {
    data: plumberPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useDirectoryPlumbersInfinite(
    ['directory', 'plumbers', 'neighbourhood', countrySlug, countySlug, neighbourhoodSlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
    fetchPlumbers,
    !!(countrySlug && countySlug && neighbourhoodSlug),
  )

  const plumbers = plumberPages?.pages.flatMap(p => p.plumbers) ?? []
  const listTotal = plumberPages?.pages[0]?.total
  const emergencyTotal = plumberPages?.pages[0]?.emergencyTotal ?? 0

  const countryLabel       = slugToLabel(countrySlug)
  const countyLabel        = slugToLabel(countySlug)
  const neighbourhoodLabel = slugToLabel(neighbourhoodSlug)

  const hrefCtx = { basePath, countrySlug: countrySlug!, countySlug: countySlug! }

  const { data: mapData } = useQuery({
    queryKey: ['directory', 'plumbers-map', 'neighbourhood', countrySlug, countySlug, neighbourhoodSlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryPlumbersForNeighbourhood(countrySlug!, countySlug!, neighbourhoodSlug!, isEmergency, isGasSafe, isWaterSafe, { skip: 0, limit: DIRECTORY_MAP_PLUMBER_LIMIT }, {}),
    enabled: !!(countrySlug && countySlug && neighbourhoodSlug),
    staleTime: 5 * 60 * 1000,
  })
  const plumberPins = useMemo(
    () => toPlumberPins(mapData?.plumbers ?? [], p => buildPlumberHref(p, hrefCtx)),
    [mapData, basePath, countrySlug, countySlug],
  )

  return (
    <PageWrapper>
      <Seo
        title={`${label} in ${neighbourhoodLabel}, ${countyLabel} | FixDelivered`}
        description={`${listTotal ? `${listTotal} verified` : 'Find'} ${label.toLowerCase()} in ${neighbourhoodLabel}, ${countyLabel}. Many available 24/7.`}
        path={`/plumbers/${countrySlug}/${countySlug}/areas/${neighbourhoodSlug}`}
        noindex={listTotal === 0}
        breadcrumbs={[
          { name: 'UK', url: basePath },
          { name: countryLabel, url: `${basePath}/${countrySlug}` },
          { name: countyLabel, url: `${basePath}/${countrySlug}/${countySlug}` },
          { name: neighbourhoodLabel, url: `${basePath}/${countrySlug}/${countySlug}/areas/${neighbourhoodSlug}` },
        ]}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <div className={DIRECTORY_MAIN_GRID}>
          <div className="min-w-0">
            <Breadcrumb items={[
              { label: countryLabel, href: `${basePath}/${countrySlug}` },
              { label: countyLabel, href: `${basePath}/${countrySlug}/${countySlug}` },
              { label: 'Areas', href: `${basePath}/${countrySlug}/${countySlug}/areas` },
              { label: neighbourhoodLabel },
            ]} />

            <div className="mt-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900">{label} in {neighbourhoodLabel}</h1>
              <p className="text-gray-500 mt-1 text-sm min-h-[2.75rem] sm:min-h-[1.5rem]">
                {listTotal && listTotal > 0
                  ? `${listTotal} verified plumbers${emergencyTotal > 0 ? ` · ${emergencyTotal} available 24/7` : ''}. `
                  : ''}
                Local plumbers serving the {neighbourhoodLabel} area.
              </p>
              <DirectorySearchBar className="mt-3" />
            </div>

            <DirectoryPlumberList
              plumbers={plumbers}
              isLoading={isLoading}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              listTotal={listTotal}
              extraFilters={extraFilters}
              onFiltersChange={setExtraFilters}
              hrefBuilder={p => buildPlumberHref(p, hrefCtx)}
              heading={`Plumbers in ${neighbourhoodLabel}`}
              emptyAreaLabel={neighbourhoodLabel}
              backLink={{ to: `${basePath}/${countrySlug}/${countySlug}/areas`, label: `Browse all areas in ${countyLabel}` }}
            />
          </div>

          <div className={DIRECTORY_SIDEBAR_COL}>
            <DirectorySidebar
              count={listTotal}
              areaLabel={neighbourhoodLabel}
              emergencyCount={emergencyTotal}
              plumberPins={plumberPins}
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}
